import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductById } from "../../../slice/product/productSlice";

/* ── FSSAI food type — the mark is drawn, never an emoji ── */
const FOOD_TYPE_OPTIONS = [
  { value: "veg",    label: "Veg",          color: "#0A8F3C", bg: "#EAF6EF", border: "#B7E0C6" },
  { value: "nonveg", label: "Non-veg",      color: "#C6362C", bg: "#FBECEA", border: "#F0C3BD" },
  { value: "egg",    label: "Contains egg", color: "#C9952A", bg: "#FBF3E2", border: "#EFD18C" },
];
/* absent/unknown values fall back to veg — the catalog is vegetarian by default */
const normalizeFoodType = (v) => {
  const s = String(v || "").toLowerCase();
  return FOOD_TYPE_OPTIONS.some((o) => o.value === s) ? s : "veg";
};

/* ══ FSSAI Food-type Mark ══
   Square outline with a filled circle centred inside — drawn in CSS, never an
   emoji. 1.5px border, 2px corners, dot at 45% of the square's inner width. */
const FoodTypeMark = ({ type = "veg", size = 16, style }) => {
  const { color } = FOOD_TYPE_OPTIONS.find((o) => o.value === type) ?? FOOD_TYPE_OPTIONS[0];
  const dot = Math.max(3, Math.round((size - 3) * 0.45));
  return (
    <span
      aria-hidden="true"
      style={{
        width: size, height: size, flexShrink: 0, boxSizing: "border-box",
        border: `1.5px solid ${color}`, borderRadius: 2, background: "#fff",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        ...style,
      }}>
      <span style={{ width: dot, height: dot, borderRadius: "50%", background: color }} />
    </span>
  );
};

const ViewProduct = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id }   = useParams();
  const [mainImage, setMainImage] = useState(0);

  const { currentProduct: p, loading, error } = useSelector((s) => s.product);

  useEffect(() => { dispatch(fetchProductById(id)); }, [dispatch, id]);


const BASE_URL = process.env.REACT_APP_API_URL;
  const imgSrc = (src) =>
    !src ? "/assets/images/placeholder.png"
    : src.startsWith("http") ? src
    : `${BASE_URL}/${src}`;

  if (loading) return (
    <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
      <div className="spinner-border text-primary" role="status" />
    </div>
  );

  if (error || !p) return (
    <div className="container-fluid">
      <div className="alert alert-danger mt-4">{error ?? "Product not found"}</div>
    </div>
  );

const variant = p.variant?.name || "";
const mrp = p.mrp ?? 0;
const sellingPrice = p.sellingPrice ?? 0;
  const discount       = mrp > sellingPrice ? Math.round(((mrp - sellingPrice) / mrp) * 100) : 0;

// admin CRUD returns foodType; food_type is the app-facing spelling
const foodType = normalizeFoodType(p.foodType ?? p.food_type);
const foodTypeOption = FOOD_TYPE_OPTIONS.find((o) => o.value === foodType) ?? FOOD_TYPE_OPTIONS[0];

  /* ── SALE CHANNELS ──
     One product can sell two ways at once and the two stock pools are
     independent — packs on the shelf (variants[].stock) and a bulk bin
     milled to order (StockEntry.currentStock, in kg). They are never
     added together and nothing here moves stock between them.

     `channels` comes from the API; the productType/isMillable fallback
     keeps this correct on a server that predates the contract. */
  const channels = (() => {
    const c = p.channels;
    if (c && (typeof c.bulkMilling === "boolean" || typeof c.prepacked === "boolean")) {
      return { bulk: !!c.bulkMilling, shelf: !!c.prepacked };
    }
    const type  = p.productType;
    const bulk  = type === "rawMaterial" || !!p.isMillable || !!p.isRoastable;
    const shelf = type
      ? type !== "rawMaterial"
      : Array.isArray(p.variants) && p.variants.length > 0;
    return { bulk, shelf };
  })();

  const channelLabel =
    channels.shelf && channels.bulk ? "Shelf + Bulk"
    : channels.shelf ? "Shelf"
    : channels.bulk ? "Bulk"
    : "—";

  /* the shelf number (packs) and the bulk bin, reported separately */
  const packsTotal = Number.isFinite(Number(p.stock_total)) ? Number(p.stock_total) : null;
  const bulkStock  = Number.isFinite(Number(p.bulk_stock))
    ? Number(p.bulk_stock)
    : channels.bulk && !channels.shelf
    ? packsTotal                                   // bulk-only: stock_total IS the bin
    : Number.isFinite(Number(p.currentStock))
    ? Number(p.currentStock)
    : null;

  return (
    <div>
      {/* Page Title */}
   
      <div className="container-fluid " style={{position:"relative",top:"15px",marginBottom:"20px"}} >

        {/* Top bar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <button onClick={() => navigate(-1)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--mly-heading)", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            ← Back to Products
          </button>
          <button className="btn btn-primary btn-sm"
            onClick={() => navigate(`/admin/products/edit/${p._id}`)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            ✏️ Edit Product
          </button>
        </div>

        {/* Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 20, alignItems: "start" }}>

          {/* ── LEFT: Images ── */}
          <div>
            <div className="card" style={{ borderRadius: 16, overflow: "hidden" }}>
              {/* Main image */}
              <div style={{ background: "var(--mly-surface-2)", display: "flex", alignItems: "center", justifyContent: "center", height: 280, position: "relative" }}>
                {discount > 0 && (
                  <span style={{ position: "absolute", top: 14, left: 14, background: "#ef4444", color: "#fff", fontSize: 11, fontWeight: 800, padding: "4px 10px", borderRadius: 8 }}>
                    -{discount}%
                  </span>
                )}
                <span style={{
                  position: "absolute", top: 14, right: 14,
                  background: p.status ? "#f0fdf4" : "#fef2f2",
                  color: p.status ? "#16a34a" : "#dc2626",
                  border: `1px solid ${p.status ? "#bbf7d0" : "#fecaca"}`,
                  fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                }}>
                  {p.status ? "Active" : "Inactive"}
                </span>
             <img
  src={
    p.images?.length > 0
      ? `${BASE_URL}/uploads/${p.images[mainImage]}`
      : ""
  }
  alt={p.name}
  style={{
    maxHeight: 240,
    maxWidth: "100%",
    objectFit: "contain"
  }}

/>
              </div>
{/* Thumbnails */}
{p.images?.length > 1 && (
  <div
    style={{
      display: "flex",
      gap: 10,
      padding: "14px 16px",
      borderTop: "1px solid var(--mly-border-soft)",
      flexWrap: "wrap",
    }}
  >
    {p.images.map((img, idx) => (
      <div
        key={idx}
        onClick={() => setMainImage(idx)}
        style={{
          width: 60,
          height: 60,
          borderRadius: 10,
          overflow: "hidden",
          cursor: "pointer",
          border:
            mainImage === idx
              ? "2px solid var(--mly-accent)"
              : "1.5px solid var(--mly-border)",
          background: "var(--mly-surface-2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <img
          src={`${BASE_URL}/uploads/${img}`}
          alt={`thumb-${idx}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.target.src =
              "/assets/images/placeholder.png";
          }}
        />
      </div>
    ))}
  </div>
)}
            </div>

            {/* Processing badges */}
            {(p.isMillable || p.isRoastable) && (
              <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                {p.isMillable && (
                  <div className="card" style={{ flex: 1, borderRadius: 12, padding: "12px 16px", textAlign: "center", overflow: "hidden" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--mly-text-muted)", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>Millable</div>
                    <span style={{ background: "#f0fdf4", color: "#16a34a", fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 20, border: "1px solid #bbf7d0" }}>YES</span>
                  </div>
                )}
                {p.isRoastable && (
                  <div className="card" style={{ flex: 1, borderRadius: 12, padding: "12px 16px", textAlign: "center", overflow: "hidden" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--mly-text-muted)", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>Roastable</div>
                    <span style={{ background: "#fff7ed", color: "#ea580c", fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 20, border: "1px solid #fed7aa" }}>YES</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── RIGHT: Info ── */}
          <div>

            {/* Main Info */}
            <div className="card" style={{ borderRadius: 16, marginBottom: 16 }}>
              <div style={{ padding: "22px 24px 20px" }}>

                {/* Category + Sub */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                  <span style={{ background: "var(--mly-accent-soft)", color: "var(--mly-heading)", fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20, border: "1px solid var(--mly-accent)" }}>
                    📁 {p.category?.name ?? "—"}
                  </span>
                  {p.subCategory?.name && (
                    <span style={{ background: "var(--mly-surface-2)", color: "var(--mly-text-muted)", fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 20, border: "1px solid var(--mly-border)" }}>
                      {p.subCategory.name}
                    </span>
                  )}
                </div>

                {/* Name — FSSAI mark sits inline before it, as in the app */}
                <h2 style={{ fontSize: 22, fontWeight: 800, color: "var(--mly-heading)", marginBottom: 16, lineHeight: 1.3 }}>
                  <FoodTypeMark type={foodType} size={14} style={{ marginRight: 8, verticalAlign: "middle" }} />
                  {p.name}
                </h2>

                {/* Price */}
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
                  <span style={{ fontSize: 28, fontWeight: 800, color: "var(--mly-text)" }}>₹{Number(sellingPrice).toLocaleString()}</span>
                  {mrp > sellingPrice && (
                    <span style={{ fontSize: 16, color: "var(--mly-text-muted)", textDecoration: "line-through" }}>₹{Number(mrp).toLocaleString()}</span>
                  )}
                  {discount > 0 && (
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#16a34a", background: "#dcfce7", padding: "3px 10px", borderRadius: 20 }}>Save {discount}%</span>
                  )}
                </div>

                {/* Max Offer */}
                {p.maxOffer > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    <span style={{ background: "var(--mly-accent-soft)", color: "var(--mly-heading)", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, border: "1px solid var(--mly-accent)" }}>
                      Max {p.maxOffer}% offer
                    </span>
                  </div>
                )}

                <hr style={{ border: "none", borderTop: "1px solid var(--mly-border-soft)", margin: "0 0 18px" }} />

                {/* Description */}
                {p.description && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "var(--mly-text-muted)", textTransform: "uppercase", marginBottom: 8 }}>Description</div>
                    <p style={{ fontSize: 14, color: "var(--mly-text-soft)", lineHeight: 1.7, margin: 0 }}>{p.description}</p>
                  </div>
                )}

                {/* Variants */}
              {p.variant?.name && (
  <div style={{ marginBottom: 20 }}>
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1,
        color: "var(--mly-text-muted)",
        textTransform: "uppercase",
        marginBottom: 10
      }}
    >
      Variant
    </div>

    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: "var(--mly-accent-soft)",
        color: "var(--mly-heading)",
        border: "1px solid var(--mly-accent)",
        borderRadius: 20,
        padding: "8px 16px",
        fontSize: 13,
        fontWeight: 700
      }}
    >
      {p.variant.name}
    </div>
  </div>
)}

                {/* Sale channels — two independent stock pools, never summed */}
                <div>
                  <div
                    style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: 1,
                      color: "var(--mly-text-muted)", textTransform: "uppercase", marginBottom: 10,
                    }}
                  >
                    Sale Channels
                  </div>

                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    <span
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: channels.shelf && channels.bulk ? "var(--mly-surface-2)" : channels.shelf ? "#f0fdf4" : "#fff7ed",
                        color: channels.shelf && channels.bulk ? "var(--mly-heading)" : channels.shelf ? "#16a34a" : "#b45309",
                        border: `1px solid ${channels.shelf && channels.bulk ? "var(--mly-accent)" : channels.shelf ? "#bbf7d0" : "#fed7aa"}`,
                        borderRadius: 20, padding: "8px 16px", fontSize: 13, fontWeight: 700,
                      }}
                    >
                      {channelLabel}
                    </span>

                    {channels.shelf && packsTotal !== null && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: "var(--mly-surface-2)", color: "var(--mly-text)", border: "1px solid var(--mly-border)",
                        borderRadius: 20, padding: "8px 16px", fontSize: 13, fontWeight: 600,
                      }}>
                        Packs on shelf <b>{packsTotal}</b>
                      </span>
                    )}

                    {channels.bulk && bulkStock !== null && (
                      <span
                        title="Bulk bin for custom milling — a pool of its own, separate from the packs"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          background: "#fff7ed", color: "#b45309", border: "1px solid #fed7aa",
                          borderRadius: 20, padding: "8px 16px", fontSize: 13, fontWeight: 600,
                        }}
                      >
                        Bulk bin <b>{bulkStock} kg</b>
                      </span>
                    )}
                  </div>

                  {channels.shelf && channels.bulk && (
                    <div style={{ fontSize: 11.5, color: "var(--mly-text-muted)", marginTop: 8 }}>
                      Pack stock and bulk stock are managed separately — this system never moves stock between them.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Details Card */}
            <div className="card" style={{ borderRadius: 16 }}>
              <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--mly-border-soft)" }}>
                <h6 style={{ margin: 0, fontWeight: 700, color: "var(--mly-heading)", fontSize: 14 }}>Product Details</h6>
              </div>
              <div style={{ padding: "4px 20px 8px" }}>
                {[
                  { icon: "🏷️", label: "Tax Structure",  value: p.taxStructure ? `${p.taxStructure.taxName} (${p.taxStructure.percentage}%)` : "—" },
                  { icon: "📁", label: "Category",       value: p.category?.name ?? "—" },
                  { icon: "📂", label: "Sub Category",   value: p.subCategory?.name ?? "—" },
                  // the FSSAI mark stands in for the row icon — it is drawn, never an emoji
                  { icon: <FoodTypeMark type={foodType} size={16} />, label: "Food Type",
                    value: <span style={{ color: foodTypeOption.color, fontWeight: 700 }}>{foodTypeOption.label}</span> },
                  { icon: "🌾", label: "Sale Channels",   value: channelLabel },
                  { icon: "✅", label: "Status",         value: p.status ? "Active" : "Inactive" },
                  { icon: "👁️", label: "Visibility",     value: p.visibility ? "Visible" : "Hidden" },
                  { icon: "👤", label: "Created By",     value: p.createdBy ?? "—" },
                  { icon: "🕐", label: "Created At",     value: p.createdAt ? new Date(p.createdAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" }) : "—" },
                  { icon: "👤", label: "Updated By",     value: p.updatedBy ?? "—" },
                  { icon: "🕐", label: "Updated At",     value: p.updatedAt ? new Date(p.updatedAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric" }) : "—" },
                ].map(({ icon, label, value }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 0", borderBottom: "1px solid var(--mly-border-soft)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ width: 30, height: 30, borderRadius: 8, background: "var(--mly-accent-soft)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>{icon}</span>
                      <span style={{ fontSize: 13, color: "var(--mly-text-soft)" }}>{label}</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--mly-text)" }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewProduct;